/**
 * API client for the institution onboarding endpoints (admin backend, port 8001).
 *
 * These endpoints take the institution id in the PATH (not the X-Institution-Id
 * header) so the console can inspect any institution's onboarding state.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

export async function fetchOnboardingStatus(institutionId) {
  const res = await fetch(`${API_URL}/api/institutions/${institutionId}/onboarding-status`);
  if (!res.ok) throw new Error(`Failed to fetch onboarding status: ${res.status}`);
  return res.json();
}

export async function fetchInstitutionConfig(institutionId) {
  const res = await fetch(`${API_URL}/api/institutions/${institutionId}/config`);
  if (!res.ok) throw new Error(`Failed to fetch institution config: ${res.status}`);
  return res.json();
}

export async function fetchDataHealth(institutionId) {
  const res = await fetch(`${API_URL}/api/institutions/${institutionId}/data-health`);
  if (!res.ok) throw new Error(`Failed to fetch data health: ${res.status}`);
  return res.json();
}

// --- Onboarding-run manifest (W41 41.2) -----------------------------------

async function jsonOrThrow(res, label) {
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body.error ? `: ${body.error}` : '';
    } catch (e) { /* non-JSON error body */ }
    throw new Error(`${label} failed (${res.status})${detail}`);
  }
  return res.json();
}

export async function fetchRuns(slug) {
  const q = slug ? `?slug=${encodeURIComponent(slug)}` : '';
  return jsonOrThrow(await fetch(`${API_URL}/api/onboarding/runs${q}`), 'List runs');
}

export async function fetchRun(runId) {
  return jsonOrThrow(await fetch(`${API_URL}/api/onboarding/runs/${runId}`), 'Fetch run');
}

export async function createRun(slug) {
  const res = await fetch(`${API_URL}/api/onboarding/runs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ institution_slug: slug }),
  });
  return jsonOrThrow(res, 'Create run');
}

export async function postStageEvent(runId, stageId, event, payload = {}) {
  const res = await fetch(`${API_URL}/api/onboarding/runs/${runId}/stages/${stageId}/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event, payload }),
  });
  return jsonOrThrow(res, `Stage ${stageId} ${event}`);
}

export async function fetchPacket(runId, stageId) {
  return jsonOrThrow(
    await fetch(`${API_URL}/api/onboarding/runs/${runId}/stages/${stageId}/packet`),
    'Fetch packet'
  );
}

export async function fetchStageChecks(runId, stageId) {
  return jsonOrThrow(
    await fetch(`${API_URL}/api/onboarding/runs/${runId}/stages/${stageId}/checks`),
    'Fetch checks'
  );
}

// --- A1 console executor (W41.4 Track B) -----------------------------------

// Dispatch a console action for a stage; returns the job snapshot (202).
export async function executeStage(runId, stageId, action = null) {
  const res = await fetch(
    `${API_URL}/api/onboarding/runs/${runId}/stages/${stageId}/execute`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(action ? { action } : {}),
    }
  );
  return jsonOrThrow(res, `Execute ${stageId}`);
}
